import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllPosts } from '../utils/blogLoader';
import { 
  ArrowRightIcon, 
  CommandLineIcon, 
  HashtagIcon 
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';

const POSTS_PER_PAGE = 5;

const formatDate = (dateStr, lang = 'en') => {
  if (!dateStr) return '';
  const locale = lang === 'et' ? 'et-EE' : 'en-GB';
  return new Date(dateStr).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const BlogImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="w-full h-64 md:w-1/3 md:h-auto bg-slate-100 flex flex-col items-center justify-center relative overflow-hidden border-b md:border-b-0 md:border-r border-slate-200 group-hover:bg-slate-50 transition-colors">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
        <CommandLineIcon className="w-12 h-12 text-slate-300 relative z-10" />
      </div>
    );
  }

  return (
    <div className="w-full h-64 md:w-1/3 md:h-auto relative bg-slate-100 overflow-hidden border-b md:border-b-0 md:border-r border-slate-200">
      <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors z-10"></div>
      <img
        src={src}
        alt={alt}
        width="400"
        height="256"
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

export default function BlogIndex() {
  const { lang } = useParams();
  const currentLang = lang || 'et';
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Blog is bilingual: show only posts written in the current language.
  const posts = getAllPosts().filter((p) => p.lang === currentLang);

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const safePage = Math.min(currentPage, totalPages || 1);
  const paginatedPosts = posts.slice((safePage - 1) * POSTS_PER_PAGE, safePage * POSTS_PER_PAGE);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [safePage]);

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO
        title={t('blog_page.index_title')}
        description={t('blog_page.index_subtitle')}
        url={`/${currentLang}/blog`}
        alternates={[
          { hreflang: 'en', href: '/en/blog' },
          { hreflang: 'et', href: '/et/blog' }
        ]}
      />

      <SchemaMarkup
        type="blog"
        data={{
          description: t('blog_page.index_subtitle'),
          url: `/${currentLang}/blog`
        }}
      />

      <PageHeader
        title={t('blog_page.index_title')}
        subtitle={t('blog_page.index_subtitle')}
        maxWidth="max-w-6xl"
      />
      
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pb-10">
            <button
              onClick={() => setSearchParams({ page: safePage - 1 })}
              disabled={safePage === 1}
              className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:border-blue-400 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setSearchParams(page === 1 ? {} : { page })}
                className={`w-9 h-9 rounded-lg border text-sm font-bold transition-colors ${
                  page === safePage
                    ? 'bg-slate-900 border-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setSearchParams({ page: safePage + 1 })}
              disabled={safePage === totalPages}
              className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:border-blue-400 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              →
            </button>
          </div>
        )}

        <div className="flex flex-col gap-10">

          {posts.length === 0 && (
            <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center font-mono text-slate-500">
              // SYSTEM_STATUS: NO_LOGS_FOUND
            </div>
          )}

          {paginatedPosts.map((post) => (
            <Link 
              to={`/${currentLang}/blog/${post.slug}`} 
              key={post.slug} 
              className="group relative bg-white rounded-3xl border border-slate-200 overflow-hidden flex flex-col md:flex-row min-h-[22rem] transition-all duration-300 hover:border-blue-400 hover:shadow-[0_0_30px_rgba(37, 99, 235,0.15)] hover:-translate-y-1"
            >
              
              {/* Image Section */}
              <BlogImage src={post.thumbnail || post.image} alt={post.title} />

              {/* Content Section */}
              <div className="flex-1 p-8 md:p-12 flex flex-col justify-between">
               
               {/* Meta Header */}
               <div className="mb-6">
                  {/* Updated Tech Date Stamp */}
                  <div className="font-mono text-xs font-bold text-blue-700 mb-4 tracking-widest uppercase flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-2 normal-case">
                      <span className="w-2 h-2 rounded-full bg-blue-600 motion-safe:animate-pulse"></span>
                      {formatDate(post.date, post.lang)}
                    </span>
                    {post.readingTime && (
                      <span className="text-slate-500 font-normal normal-case tracking-normal">{post.readingTime} {t('blog_page.min_read')}</span>
                    )}
                  </div>

                  <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4 leading-tight group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h2>
                  
                  <p className="text-slate-600 line-clamp-2 leading-relaxed text-lg">
                    {post.description}
                  </p>
               </div>

               {/* Footer: Tags & Button */}
               <div className="flex flex-wrap items-end justify-between gap-6 mt-auto">
                  
                  {/* Tech Chips */}
                  <div className="flex flex-wrap gap-2">
                    {(post.tags || []).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs font-mono font-bold uppercase tracking-wider group-hover:border-blue-200 group-hover:bg-blue-50/50 group-hover:text-blue-700 transition-colors">
                        <HashtagIcon className="w-3 h-3 text-slate-400 group-hover:text-blue-600" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Action Button */}
                  <div className="bg-blue-600 text-white px-6 py-3 rounded-full font-bold text-sm shadow-md flex items-center gap-2 transition-all group-hover:bg-blue-500 group-hover:shadow-lg transform group-hover:scale-105">
                     <span>{t('blog_page.read_post')}</span>
                     <ArrowRightIcon className="h-4 w-4" />
                  </div>

               </div>
              </div>
            </Link>
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setSearchParams({ page: safePage - 1 })}
                disabled={safePage === 1}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:border-blue-400 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ←
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setSearchParams(page === 1 ? {} : { page })}
                  className={`w-9 h-9 rounded-lg border text-sm font-bold transition-colors ${
                    page === safePage
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setSearchParams({ page: safePage + 1 })}
                disabled={safePage === totalPages}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:border-blue-400 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                →
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
